import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createGame, selectCell, getAIMove, legalMoves } from "./engine.js";
import type { ChessGame } from "./types.js";
import { SIZE, PIECE_EMOJIS } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "cpu" | "local" | "multiplayer";
type PlayerColor = "red" | "yellow" | "green" | "purple" | "orange" | "teal";

const COLOR_OPTIONS: PlayerColor[] = ["red", "yellow", "green", "purple", "orange", "teal"];

const CODE_WORDS = [
  "apple", "beach", "candy", "dance", "eagle", "flame", "grape", "honey",
  "ivory", "jelly", "koala", "lemon", "mango", "noble", "ocean", "pearl",
  "queen", "rain", "sugar", "tiger", "umbra", "vivid", "whale", "xenon",
  "yacht", "zebra", "bloom", "coral", "dawn", "ember", "frost", "glow",
  "haven", "iris", "jade", "kite", "lune", "mist", "nova", "opal",
];

function generateCode(): string {
  const pick = () => CODE_WORDS[Math.floor(Math.random() * CODE_WORDS.length)];
  return `${pick()}-${pick()}-${pick()}`;
}

export default function ChessPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("cpu");
  const [p1Name, setP1Name] = useState("Player 1");
  const [p2Name, setP2Name] = useState("Player 2");
  const [game, setGame] = useState<ChessGame>(() => createGame());

  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<1 | 2 | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");
  const [joinCode, setJoinCode] = useState(generateCode);
  const [p1Color, setP1Color] = useState<PlayerColor>("red");

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  const mpConnectedRef = useRef(mpConnected);

  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;
  mpConnectedRef.current = mpConnected;

  const p1 = p1Name.trim() || "Player 1";
  const p2 = mode === "cpu" ? "Computer" : p2Name.trim() || "Player 2";
  const isCpuTurn = mode === "cpu" && game.turn === "black" && !game.winner && !game.isDraw;

  const status = useMemo(() => {
    if (game.winner === "white") return `${p1} wins!`;
    if (game.winner === "black") return `${mode === "cpu" ? "Computer" : p2} wins!`;
    if (game.isDraw) return "Draw game.";
    return `${game.turn === "white" ? p1 : p2}'s turn${game.inCheck ? " — Check!" : ""}`;
  }, [game, p1, p2, mode]);

  const isMyTurn = mode !== "multiplayer"
    ? (mode === "cpu" ? game.turn === "white" : true)
    : yourTurn;

  function handleCellClick(r: number, c: number) {
    if (game.winner || game.isDraw) return;
    if (mode === "multiplayer" && !yourTurn) return;
    if (isCpuTurn) return;
    if (mode === "multiplayer") {
      const piece = game.board[r][c];
      if (piece && ((mpPlayerId === 1 && piece.color === "white") || (mpPlayerId === 2 && piece.color === "black"))) {
        const moves = legalMoves(game.board, r, c);
        setGame({ ...game, selected: { r, c }, validMoves: moves });
      } else if (game.selected) {
        const move = game.validMoves.find((m) => m.r === r && m.c === c);
        if (move) {
          apiPost("/api/chess/move", { code: roomCode, playerId: mpPlayerId, token: mpToken, fr: game.selected.r, fc: game.selected.c, tr: r, tc: c });
          setGame({ ...game, selected: null, validMoves: [] });
        }
      }
      return;
    }
    setGame((prev) => selectCell(prev, r, c));
  }

  function resetGame() {
    if (mode === "multiplayer") {
      apiPost("/api/chess/reset", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    setGame(createGame());
  }

  function handleModeChange(next: Mode) {
    if (mode === "multiplayer") {
      if (roomCode && mpToken) apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      stopPolling();
    }
    setMode(next);
    setGame(createGame());
  }

  useEffect(() => {
    if (!isCpuTurn) return;
    const timer = window.setTimeout(() => {
      setGame((prev) => {
        const move = getAIMove(prev);
        if (!move) return prev;
        const withSelected = selectCell(prev, move.fr, move.fc);
        return selectCell(withSelected, move.tr, move.tc);
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [isCpuTurn, game.moveCount]);

  // ---- multiplayer --------------------------------------------------------

  async function apiPost(path: string, body: object) {
    try {
      const res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      return await res.json();
    } catch { return { type: "error", message: "Network error." }; }
  }

  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined":
        setOpponentName(event.playerName);
        setMpConnected(true);
        setMessage(`Opponent ${event.playerName} joined! Press New Game to start.`);
        break;
      case "game-state":
        setGame(event.game);
        setYourTurn(event.yourTurn);
        break;
      case "opponent-left":
        setMpConnected(false); setRoomCode(""); setOpponentName(""); setMpPlayerId(null); setMpToken("");
        setMessage("Opponent left the game."); setGame(createGame());
        break;
      case "error":
        setMpError(event.message); break;
    }
  }

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    async function poll() {
      if (!pollingRef.current) return;
      const code = roomCodeRef.current, pid = mpPlayerIdRef.current;
      if (!code || !pid) { pollingRef.current = false; return; }
      try {
        const res = await fetch(`/api/poll?code=${encodeURIComponent(code)}&playerId=${pid}`);
        const data = await res.json();
        for (const event of data.events) { handleServerEvent(event); if (event.type === "opponent-left") { pollingRef.current = false; return; } }
      } catch { /* retry */ }
      if (pollingRef.current) pollRef.current = setTimeout(poll, 200);
    }
    poll();
  }, []);

  function stopPolling() { pollingRef.current = false; if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } }
  useEffect(() => { return () => stopPolling(); }, []);

  const [message, setMessage] = useState("");

  async function handleCreateRoom() {
    const result = await apiPost("/api/create-room", { playerName: p1, playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = result.code; mpPlayerIdRef.current = result.playerId;
    setRoomCode(result.code); setMpPlayerId(result.playerId); setMpToken(result.token);
    setMessage(`Room created! Share this code: ${result.code}`); startPolling();
  }

  async function handleJoinRoom() {
    if (!joinCode.trim()) { setMpError("Enter a room code."); return; }
    const result = await apiPost("/api/join-room", { code: joinCode.trim(), playerName: p1, playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = joinCode.trim(); mpPlayerIdRef.current = result.playerId;
    setMpPlayerId(result.playerId); setMpToken(result.token); setRoomCode(joinCode.trim());
    setOpponentName(result.opponentName); setP1Color(result.opponentColor);
    setMpConnected(true); setMessage(`Connected with ${result.opponentName}. Press New Game to start!`);
    startPolling();
  }

  async function handleLeaveGame() {
    await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
    stopPolling(); setMpConnected(false); setRoomCode(""); setOpponentName(""); setMpPlayerId(null); setMpToken("");
    setGame(createGame()); setMessage("Left the game.");
  }

  return (
    <div className="game-shell">
      <ConfettiSplash active={!!game.winner} winnerName={game.winner === "white" ? p1 : game.winner === "black" ? (mode === "cpu" ? "Computer" : p2) : undefined} />
      {!hideTitle && <h1 className="title">Chess</h1>}
      <section className="control-panel">
        <div className="row two-col">
          <label>Mode <select value={mode} onChange={(e) => handleModeChange(e.target.value as Mode)}>
            <option value="cpu">vs Computer</option>
            <option value="local">Two Players Local</option>
            <option value="multiplayer">Multiplayer Online</option>
          </select></label>
        </div>
        <div className="row two-col">
          <label>{mode === "multiplayer" ? "Your Name" : "Player 1 (White)"} <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={20} /></label>
          {mode !== "multiplayer" && <label>{mode === "cpu" ? "Computer (Black)" : "Player 2 (Black)"} <input value={mode === "cpu" ? "Computer" : p2Name} onChange={(e) => setP2Name(e.target.value)} maxLength={20} disabled={mode === "cpu"} /></label>}
        </div>
        {mode === "multiplayer" && !mpConnected && (
          <div className="row two-col"><label>Your Color <select value={p1Color} onChange={(e) => setP1Color(e.target.value as PlayerColor)}>{COLOR_OPTIONS.map((c) => (<option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>))}</select></label></div>
        )}
        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="row two-col"><label>Room Code <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. apple-blue-rain" maxLength={40} /></label></div>
        )}
        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="mp-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="mp-btn" onClick={handleCreateRoom}>Create Room</button>
            <button className="mp-btn mp-btn-secondary" onClick={handleJoinRoom}>Join Room</button>
          </div>
        )}
        {mode === "multiplayer" && roomCode && !mpConnected && (
          <div style={{ textAlign: 'center' }}>
            <p className="mp-label">Share this code with a friend:</p>
            <p className="mp-code">{roomCode}</p>
            <p className="mp-waiting">Waiting for opponent to join...</p>
            <button className="mp-cancel" onClick={handleLeaveGame}>Cancel</button>
          </div>
        )}
        {mode === "multiplayer" && mpConnected && (
          <div className="mp-connected-bar"><span className="mp-connected-info">Connected with <strong>{opponentName}</strong></span></div>
        )}
        <div className="actions">
          {(mode !== "multiplayer" || mpConnected) && <button onClick={resetGame}>New Game</button>}
          {mode === "multiplayer" && mpConnected && <button className="mp-leave" onClick={handleLeaveGame}>Leave Game</button>}
        </div>
        {mpError && <p className="subtle mp-error">{mpError}</p>}
      </section>

      <section className="board-panel">
        <p className="status">{status}</p>
        <div className="chess-board">
          {game.board.map((row, r) =>
            row.map((cell, c) => {
              const dark = (r + c) % 2 === 1;
              const isValid = game.validMoves.some((m) => m.r === r && m.c === c);
              const isSel = game.selected?.r === r && game.selected?.c === c;
              const isLast = game.lastMove && ((game.lastMove.from.r === r && game.lastMove.from.c === c) || (game.lastMove.to.r === r && game.lastMove.to.c === c));
              const isCheck = cell?.type === "king" && game.inCheck && cell.color === game.turn;
              const classes = `chess-cell ${dark ? "dark" : "light"}${isSel ? " selected" : ""}${isValid ? " valid" : ""}${isLast ? " last-move" : ""}${isCheck ? " in-check" : ""}`;
              const emoji = cell ? PIECE_EMOJIS[`${cell.color}-${cell.type}`] || "" : "";
              return (
                <div key={`${r}-${c}`} className={classes} onClick={() => handleCellClick(r, c)}>
                  {emoji && <span className="chess-piece">{emoji}</span>}
                </div>
              );
            })
          )}
        </div>
        <div className="how-to-play">
          <p className="how-title">How to Play</p>
          <p>1. Click a piece, then click a highlighted square to move.</p>
          <p>2. Capture opponent's pieces by moving onto them. Pawns promote to Queen.</p>
          <p>3. Checkmate the enemy king to win. Stalemate is a draw.</p>
        </div>
      </section>
    </div>
  );
}
